// S2 — WASTE LEDGER, /waste/events (Module 05 §4, slice 05-B).
//
// The screen is the fact stream: one row per §2.3 event, newest first, cursor
// pages, never a client re-sort. The drawer carries the two decisions the row
// admits — disposition (once, §3.3's one-shot rule renders as a form that
// disappears after) and reversal (the correction PAIR: the original is never
// mutated, and both rows stay on the ledger netting to zero).
//
// MONEY (§7): a `*_minor` key ABSENT from the payload means "not yours to see",
// and the cell renders the redaction mark — never 0, never blank (a blank cost
// column reads as free waste). The predicate is the payload itself, not a
// client-side role guess: the serializer already decided.
//
// "Add to scrap sale" (§4's bulk row) is 05-C's — `waste_sales` does not exist
// — and the toolbar SAYS so rather than shelling a disabled button (D-033).
// CSV exports the FULL filtered result, walked server-side through the same
// cursor read the screen uses (GM-week audit, 2026-08: the export used to
// serialize only the cursor pages already on screen — 50 rows at a time — so a
// month-end export silently claimed completeness over whatever happened to be
// loaded). The walk re-issues the SAME reviewed query with the SAME filters;
// only the paging differs, so it is not a second, wider query — and when the
// honesty cap bites, the screen says so instead of shipping a silently
// partial file. Details at `walkWasteEventsForExport` below.
//
// UX-4 — WHAT THE MIGRATION CHANGED, and the two honesty defects it found:
//
//  * The hand-rolled `<table class="waste-table">` (with its own ≤640 px
//    collapse block) is the §3.3 `DataTable`, so the phone contract is the
//    primitive's; the filters are `Field` inside the register's `.filter-bar`;
//    the buttons and status pills are `Button` / `StatusChip`.
//  * A NULL CAUSE RENDERED AS AN EMPTY CELL. A category with
//    `requires_cause: false` legitimately has no reason, and a row whose cause
//    the catalog has not resolved yet has an unknown one — both painted the
//    same blank. They now read "—" and the resolving marker respectively
//    (`RefLabel`), which is the same rule module 04 applies to a work centre.
//  * THE DRAWER PRINTED A RAW UUID WHERE A SHIFT NAME BELONGS. The shift
//    instance id is a wire identifier; the operator's word for that shift is
//    its DEFINITION CODE, which this screen already loads for the filter's
//    option list. The name is now first and the id follows it as a `<code>`
//    identifier — deliberately in full, because it is the value a supervisor
//    pastes into the filter, and because two certified walkthroughs assert the
//    drawer names the instance they flagged (M05-S01, M05-S08). The RESIDUAL is
//    reported, not hidden: on a facility the picker has not resolved, the id is
//    still the only thing the screen can say.

import { shortId } from "../../../../lib/short-id";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EvidencePhotos } from "../../../../components/evidence-photos";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { client } from "../../../../lib/api";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { requestIdOf, unwrap } from "../../../../lib/request";
// The ONE csv helper (inventory's lib, the area precedent for shared office
// libs — this screen already leans on production's). `toCsv` carries the
// RFC 4180 escaping + the formula-injection guard (via its `csvCell`),
// `downloadCsv` carries the UTF-8 BOM. The GM-week audit found this screen's
// export doing neither: raw
// `.join(",")` (no escaping at all) and a bare Blob, so Cyrillic — the design
// partner's own uom strings and any Russian note that later joins the columns
// — opened as mojibake in Excel, which is THE consumer of a month-end export
// at a Kazakh factory. One escaper, one BOM, one download dance — a second
// hand-rolled copy here is how the two would drift apart again.
import {
  downloadCsv,
  EXPORT_PAGE_SIZE,
  EXPORT_ROW_CAP,
  toCsv,
  walkCursorForExport,
  csvConventionFor,
  type CsvConvention,
} from "../../../../lib/csv";
import {
  facilityDayEndUtcMs,
  facilityDayStartUtcMs,
  facilityTodayIso,
} from "../../../../lib/business-dates";
import { FacilityFilter } from "../../production/components/facility-filter";
import { MutationFailureNote } from "../mutation-failure-note";
// The REGISTER's `RefLabel` (components/), not module 04's copy. The module-04
// one paints its absent state with `.production-hint`, a class declared only as
// `.production-area .production-hint` — so on a `.waste-area` screen the "—"
// resolved to `--text-primary` with no title attribute, while the same absent
// state one column over (rendered by the shared primitive) was read-only ink
// with `title="Nothing recorded"`. One honesty fix, half-landed, inside one
// screen. The shared primitive's own header names this exact failure mode as
// its reason for existing.
import { RefLabel } from "../../../../components/ref-label";
import { useFacilities, useWorkCenters } from "../../production/hooks/production-queries";
import { formatMinor, formatQty, formatTimestamp, localeLabel } from "../../production/lib/format";
import { isWasteReadRefused, useWastePermissions } from "../hooks/permissions";
import { useShiftLabels } from "../hooks/shift-labels";
import {
  useReverseWasteEvent,
  useSetWasteDisposition,
  useWasteCatalog,
  useWasteEvents,
  type WasteEvent,
  type WasteEventsQuery,
} from "../hooks/waste-queries";

export interface WasteEventsSearch {
  facility_id?: string;
  occurred_from?: string;
  occurred_to?: string;
  // 05-B-2 (§4 S2): the three filters that were API-only until this sub-step.
  // They survive URL validation so S4's drill link and module 04's WO surfaces
  // can POINT the ledger at a work centre / shift / work order.
  work_center_id?: string;
  shift_instance_id?: string;
  work_order_id?: string;
  /** S13's drill target (2026-08-20). The cause CONTROL has existed on this
   * screen since 05-B-2; it was the only one of the seven not URL-backed, so
   * the Pareto — whose entire purpose is "this cause cost you 900 000, go look
   * at it" — had nowhere to send a click. A ranked board that does not open is
   * a poster (the same finding S4's hand-off number carried, D-140). */
  cause_code_id?: string;
}

export function validateWasteEventsSearch(search: Record<string, unknown>): WasteEventsSearch {
  const out: WasteEventsSearch = {};
  for (const key of [
    "facility_id",
    "occurred_from",
    "occurred_to",
    "work_center_id",
    "shift_instance_id",
    "work_order_id",
    "cause_code_id",
  ] as const) {
    const value = search[key];
    if (typeof value === "string" && value.length > 0) out[key] = value;
  }
  return out;
}

/** The §2.3 uuid shape, loosely (any version): the WO filter is a paste-an-id
 * control, and a half-typed value must not be sent as a filter that 422s. */
export const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** The redaction mark (§4: money renders `•••` for roles without cost
 * visibility). Absence IS the signal — the serializer dropped the key. */
export function moneyCell(minor: number | undefined, currency: string, locale: string): string {
  return minor === undefined ? "•••" : formatMinor(minor, currency, locale);
}

/** The ledger CSV (redacted money exports as the mark — an export must not
 * claim a number the serializer withheld from this role). Escaping and CRLF
 * discipline come from the shared csv helper: the old hand-rolled `.join(",")`
 * would shear a row in two on the first comma inside a value. */
export function eventsCsv(rows: readonly WasteEvent[], convention: CsvConvention): string {
  return toCsv(
    [
      "event_no",
      "occurred_at",
      "source",
      "category_id",
      "cause_code_id",
      "item_id",
      "qty",
      "uom",
      "cost_minor",
      "currency",
      "disposition",
      "work_order_id",
      // D-102's attribution, in the file the month-end audit is actually done
      // in. The drawer's chip had the same hole and is fixed in the same
      // change: a hand-off whose origin the ledger knows and the export drops
      // is an attribution that survives on screen and dies in the spreadsheet
      // — and the spreadsheet is where the argument about whose scrap it was
      // gets settled. Ids, not labels: an export is reconciled against the
      // wire, and the shift-code join is a display concern that belongs to the
      // screen.
      "shift_instance_id",
      "originating_shift_instance_id",
      "reversed",
    ],
    rows.map((row) => [
      row.event_no,
      row.occurred_at,
      row.source,
      row.category_id,
      row.cause_code_id,
      row.item_id,
      row.qty,
      row.uom,
      row.cost_minor ?? "•••",
      row.currency,
      row.disposition,
      row.work_order_id,
      row.shift_instance_id,
      row.originating_shift_instance_id,
      row.reversed_by_event_id !== null || row.reverses_event_id !== null ? "yes" : "",
    ]),
    // `qty` is the only decimal quantity; `cost_minor` is integer minor units
    // beside a currency column and reads as a number in every locale (D-289
    // ruling 3), so it is deliberately absent from this list.
    { convention, decimalHeaders: ["qty"] },
  );
}

// ---------------------------------------------------------------------------
// The export walk (GM-week audit, 2026-08). The defect it replaces: the CSV
// button serialized `rowsAccum` — the cursor pages already clicked onto the
// screen, 50 at a time — so a month-end export was a file that CLAIMED the
// filtered month and CONTAINED whatever the supervisor had happened to scroll.
// Nobody diffs a CSV against a screen; the lie surfaced only when the GM's
// month totals disagreed with finance's.
// ---------------------------------------------------------------------------

// The page size and the honesty cap are the shared export's (lib/csv.ts,
// D-289 ruling 11); re-exported so this screen's tests keep naming them.
export { EXPORT_PAGE_SIZE, EXPORT_ROW_CAP };

/**
 * Walk the FULL filtered result through the same §5 cursor read the screen
 * uses — same query params, same client, only `limit` differs — until the
 * server reports exhaustion (`next_cursor: null`) or the honesty cap is hit.
 * `capped: true` means rows exist beyond the file (never "exactly the cap
 * happened to remain"). `onProgress` fires per page so the button can count
 * while a big month walks. The walk itself is the shared one (lib/csv.ts);
 * this wrapper names the read.
 */
export async function walkWasteEventsForExport(
  query: WasteEventsQuery,
  onProgress: (loaded: number) => void,
): Promise<{ rows: WasteEvent[]; capped: boolean }> {
  return walkCursorForExport<WasteEvent>(
    async (cursor, limit) => {
      const res = await client.GET("/v1/waste/events", {
        params: { query: { ...query, limit, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not export the waste ledger");
    },
    { onProgress },
  );
}

/**
 * The download name carries the business-date range the filters asked for, so
 * July's month-end file and August's cannot both be `waste-events.csv` and
 * silently shadow each other in a Downloads folder. An unbounded edge is
 * spelled as what it is: `all` for no lower bound, today's facility date for
 * no upper (the ledger cannot contain tomorrow).
 */
export function eventsExportFilename(from: string, to: string, todayIso: string): string {
  const lower = from.length > 0 ? from : "all";
  const upper = to.length > 0 ? to : todayIso;
  return `waste-events_${lower}_${upper}.csv`;
}

/** The export's four observable states. `done.capped` is the honesty flag the
 * §4 note renders; `failed` keeps the error so the shared failure note can
 * quote the server's title + request id (never a silently missing file). */
export type EventsExportState =
  | { kind: "idle" }
  | { kind: "walking"; loaded: number }
  | { kind: "done"; exported: number; capped: boolean }
  | { kind: "failed"; error: unknown };

const SOURCES = [
  "floor_report",
  "qc_disposition",
  "calibration_variance",
  "cut_offcut",
  "count_variance",
  "byproduct_output",
  "regrind_loss",
  "manual",
] as const;

/**
 * §2.4's disposition vocabulary → the §3.5 `StatusChip` variant. Stated once so
 * a disposition can never read green on the ledger and grey in the drawer.
 *
 * The judgement calls: `pending` is AMBER because it is the one state that is
 * asking someone for a decision; `dispose` is RED because it is the outcome
 * that throws the money away; `reuse`/`regrind` are GREEN because they are the
 * recovery the module exists to measure; `sell` is ACCENT — a commercial
 * outcome, neither loss nor recovery.
 */
export function dispositionVariant(
  disposition: string,
): "ok" | "warn" | "bad" | "neutral" | "accent" {
  switch (disposition) {
    case "pending":
      return "warn";
    case "reuse":
    case "regrind":
      return "ok";
    case "sell":
      return "accent";
    case "dispose":
      return "bad";
    default:
      return "neutral";
  }
}

export function WasteEventsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/waste/events" });
  const perms = useWastePermissions();

  const facilities = useFacilities();
  const catalog = useWasteCatalog();
  const [facilityId, setFacilityId] = useState<string | undefined>(search.facility_id);
  const [from, setFrom] = useState<string>(search.occurred_from ?? "");
  const [to, setTo] = useState<string>(search.occurred_to ?? "");
  const [categoryId, setCategoryId] = useState("");
  // Seeded from the URL so S13's Pareto rows can point here (the pattern the
  // work-centre / shift / WO controls already follow).
  const [causeId, setCauseId] = useState<string>(search.cause_code_id ?? "");
  const [source, setSource] = useState("");
  const [disposition, setDisposition] = useState("");
  const [posted, setPosted] = useState("");
  // 05-B-2 — the three §4 S2 controls that had API params and no screen.
  const [workCenterId, setWorkCenterId] = useState<string>(search.work_center_id ?? "");
  const [shiftId, setShiftId] = useState<string>(search.shift_instance_id ?? "");
  const [woId, setWoId] = useState<string>(search.work_order_id ?? "");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rowsAccum, setRowsAccum] = useState<readonly WasteEvent[]>([]);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [exportState, setExportState] = useState<EventsExportState>({ kind: "idle" });

  // The station picker is facility-scoped (an all-tenant station list would be
  // ambiguous across same-coded stations), and ACTIVE only — a filter is a
  // picker, and an archived station is one nobody was sent to (the production
  // pickers' own rule).
  const workCenters = useWorkCenters(
    facilityId !== undefined && facilityId.length > 0 ? facilityId : undefined,
    "active",
  );
  // Shift options come from module 04's own board — the same read S4 joins
  // codes through, now through the ONE shared hook (hooks/shift-labels.ts):
  // both screens read the picked day AND the day before it, because §9.7 dates
  // a shift by the day it started. This screen had the two-date rule and S4 did
  // not; the rule now lives in one place and neither can drift from it.
  const facilityTimezone = facilities.data?.items.find((f) => f.id === facilityId)?.timezone;
  const shiftOptionsDate = from.length > 0 ? from : facilityTodayIso(Date.now(), facilityTimezone);
  const urlSeededShift = useMemo(() => (shiftId.length > 0 ? [shiftId] : []), [shiftId]);
  const shifts = useShiftLabels(facilityId, shiftOptionsDate, urlSeededShift);

  const woFilterValid = UUID_SHAPE.test(woId.trim());

  const query: WasteEventsQuery = useMemo(
    () => ({
      ...(facilityId !== undefined && facilityId.length > 0 ? { facility_id: facilityId } : {}),
      // D-089's other half (caught by M05-S01 on staging, 2026-08-10 00:07
      // Almaty): `${from}T00:00:00Z` staples a Z onto a FACILITY-LOCAL date,
      // shifting the window 5 h late for Almaty — an event captured between
      // 00:00 and 05:00 facility time fell outside its own day's filter. The
      // edges convert through the facility's timezone (browser-local
      // fallback per the business-dates contract when none is in hand).
      ...(from.length > 0
        ? { occurred_from: new Date(facilityDayStartUtcMs(from, facilityTimezone)).toISOString() }
        : {}),
      ...(to.length > 0
        ? { occurred_to: new Date(facilityDayEndUtcMs(to, facilityTimezone) - 1).toISOString() }
        : {}),
      ...(categoryId.length > 0 ? { category_id: categoryId } : {}),
      ...(causeId.length > 0 ? { cause_code_id: causeId } : {}),
      ...(source.length > 0 ? { source } : {}),
      ...(disposition.length > 0 ? { disposition } : {}),
      ...(posted.length > 0 ? { posted: posted as "true" | "false" } : {}),
      ...(workCenterId.length > 0 ? { work_center_id: workCenterId } : {}),
      ...(shiftId.length > 0 ? { shift_instance_id: shiftId } : {}),
      ...(woFilterValid ? { work_order_id: woId.trim() } : {}),
    }),
    [
      facilityId,
      facilityTimezone,
      from,
      to,
      categoryId,
      causeId,
      source,
      disposition,
      posted,
      workCenterId,
      shiftId,
      woFilterValid,
      woId,
    ],
  );

  // Any filter change restarts the page walk from the head — keyed on WHAT
  // the query asks, never on the memo's object identity. The memo's deps
  // include values that can change without changing the emitted params
  // (facilityTimezone arriving once the facilities list resolves, with no date
  // bounds set), and an identity-keyed reset wiped rows that had already
  // folded: the events response was on the wire with the row in it, the
  // accumulator emptied AFTER the fold, and react-query's cache key — being
  // semantic — never changed, so the fold effect had nothing new to re-fire
  // on. The screen sat on "No waste events yet" over a non-empty answer
  // (caught by M05-S08 on staging, 2026-08-14, ~01:00 Almaty: replica-green,
  // staging-red purely on response-arrival order).
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setCursors([undefined]);
    setRowsAccum([]);
    // The export notes describe the PREVIOUS filter's file. Left standing over
    // new filters, "Exported the first 10 000 rows" would be read against a
    // range it never walked.
    setExportState({ kind: "idle" });
  }, [queryKey]);

  // A read this role is refused is not ISSUED. Firing it produced four 403s
  // over ~8 s behind a loading skeleton and then an error card offering a Retry
  // that can never succeed; the screen now says so once, immediately.
  const refused = isWasteReadRefused(perms, "waste.read");
  const cursor = cursors[cursors.length - 1];
  const page = useWasteEvents(query, cursor, !refused);

  useEffect(() => {
    if (page.data === undefined) return;
    const items = page.data.items;
    // FOLD BY ID (lib/paged-rows.ts). This was append-if-unseen, which keeps
    // the STALE copy of a row the refetch updated: a disposition the server
    // accepted never reached the screen, the drawer kept offering the form, and
    // the second attempt came back 409 DISPOSITION_FINAL — a write that WORKED,
    // reported to the user as a write that failed. Same for a reversal.
    setRowsAccum((prior) => foldPagedRows(prior, items));
  }, [page.data]);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const categories = catalog.data?.categories ?? [];
  const causes = catalog.data?.causes ?? [];

  // Rev-7 FilterBar: every applied filter as a removable chip, in words.
  const filterChips: FilterChip[] = [];
  const addChip = (key: string, label: string, value: string, clear: () => void): void => {
    filterChips.push({ key, label: `${label}: ${value}`, onRemove: clear });
  };
  if (facilityId !== undefined && facilityId.length > 0) {
    const facility = facilities.data?.items.find((f) => f.id === facilityId);
    addChip("facility", t("waste.events.facility"), facility?.code ?? shortId(facilityId), () => {
      setFacilityId(undefined);
      setWorkCenterId("");
      setShiftId("");
    });
  }
  if (categoryId.length > 0) {
    const row = categories.find((c) => c.id === categoryId);
    addChip(
      "category",
      t("waste.events.category"),
      row === undefined
        ? shortId(categoryId)
        : localeLabel(row.name_i18n, i18n.language) || row.code,
      () => {
        setCategoryId("");
      },
    );
  }
  if (from.length > 0) {
    addChip("from", t("waste.events.from"), from, () => {
      setFrom("");
    });
  }
  if (to.length > 0) {
    addChip("to", t("waste.events.to"), to, () => {
      setTo("");
    });
  }
  if (causeId.length > 0) {
    const row = causes.find((c) => c.id === causeId);
    addChip(
      "cause",
      t("waste.events.colCause"),
      row === undefined ? shortId(causeId) : localeLabel(row.name_i18n, i18n.language) || row.code,
      () => {
        setCauseId("");
      },
    );
  }
  if (source.length > 0) {
    addChip("source", t("waste.events.source"), t(`waste.source.${source}`), () => {
      setSource("");
    });
  }
  if (disposition.length > 0) {
    addChip(
      "disposition",
      t("waste.events.disposition"),
      t(`waste.disposition.${disposition}`),
      () => {
        setDisposition("");
      },
    );
  }
  if (workCenterId.length > 0) {
    const wc = workCenters.data?.items.find((w) => w.id === workCenterId);
    addChip("work-center", t("waste.events.workCenter"), wc?.code ?? shortId(workCenterId), () => {
      setWorkCenterId("");
    });
  }
  if (shiftId.length > 0) {
    const option = shifts.options.find((o) => o.id === shiftId);
    addChip("shift", t("waste.events.shift"), option?.label ?? shortId(shiftId), () => {
      setShiftId("");
    });
  }
  // Only an id the query actually sends gets a chip; a half-typed one is
  // dropped from the query, and its warning stands at the field.
  if (woFilterValid) {
    addChip("wo", t("waste.events.wo"), shortId(woId.trim()), () => {
      setWoId("");
    });
  }
  if (posted.length > 0) {
    addChip(
      "posted",
      t("waste.events.posted"),
      posted === "true" ? t("waste.events.postedYes") : t("waste.events.postedNo"),
      () => {
        setPosted("");
      },
    );
  }
  const categoryName = (id: string): string | undefined => {
    const row = categories.find((c) => c.id === id);
    return row === undefined ? undefined : localeLabel(row.name_i18n, i18n.language) || row.code;
  };
  const causeName = (id: string | null): string | undefined => {
    if (id === null) return undefined;
    const row = causes.find((c) => c.id === id);
    return row === undefined ? undefined : localeLabel(row.name_i18n, i18n.language) || row.code;
  };

  const openRow = rowsAccum.find((row) => row.id === openId);
  const canDecide = perms.has("waste.disposition.set");
  const canReverse = perms.has("waste.event.reverse");

  // The export walks the SERVER, not the accumulator (GM-week audit — see the
  // walk's own header). `rowsAccum` still gates the button (an empty first
  // page IS an empty filtered result; the head of the walk and the head of
  // the screen are the same read), but the file's contents never depend on
  // how far anyone scrolled. On failure NOTHING downloads — a broken walk
  // must not quietly ship the rows it managed before dying, that is the
  // month-end lie again with a stack trace behind it.
  const exportCsv = async (): Promise<void> => {
    setExportState({ kind: "walking", loaded: 0 });
    try {
      const { rows, capped } = await walkWasteEventsForExport(query, (loaded) => {
        setExportState({ kind: "walking", loaded });
      });
      downloadCsv(
        eventsExportFilename(from, to, facilityTodayIso(Date.now(), facilityTimezone)),
        eventsCsv(rows, csvConventionFor(i18n.language)),
      );
      setExportState({ kind: "done", exported: rows.length, capped });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setExportState({ kind: "failed", error });
    }
  };

  const columns: DataTableColumn<WasteEvent>[] = [
    { key: "event_no", header: t("waste.events.colNo"), id: true },
    {
      key: "occurred_at",
      header: t("waste.events.colWhen"),
      age: true,
      render: (row) => formatTimestamp(row.occurred_at, i18n.language),
    },
    {
      key: "category_id",
      header: t("waste.events.colCategory"),
      render: (row) => (
        <RefLabel
          value={categoryName(row.category_id)}
          id={row.category_id}
          pending={catalog.isPending}
        />
      ),
    },
    {
      key: "cause_code_id",
      header: t("waste.events.colCause"),
      // A category with `requires_cause: false` HAS no reason, and a reason the
      // catalog has not answered for yet is UNKNOWN. Both used to be blank.
      render: (row) => (
        <RefLabel
          value={causeName(row.cause_code_id)}
          id={row.cause_code_id}
          pending={catalog.isPending && row.cause_code_id !== null}
        />
      ),
    },
    {
      key: "qty",
      header: t("waste.events.colQty"),
      numeric: true,
      // A quantity NEVER travels without its UoM.
      render: (row) => `${formatQty(row.qty, { locale: i18n.language })} ${row.uom}`,
    },
    {
      key: "cost_minor",
      header: t("waste.events.colCost"),
      numeric: true,
      render: (row) => (
        <span data-testid="waste-event-cost">
          {moneyCell(row.cost_minor, row.currency, i18n.language)}
        </span>
      ),
    },
    {
      key: "source",
      header: t("waste.events.colSource"),
      render: (row) => t(`waste.source.${row.source}`),
    },
    {
      key: "disposition",
      header: t("waste.events.colDisposition"),
      // BOTH HALVES OF A REVERSED PAIR SAY SO. Only the ORIGINAL used to carry
      // a chip; the reversal — which sorts FIRST, being newest — showed a
      // quantity, a reason and no marking at all, and read as a second real
      // scrap event. The rollups correctly exclude the pair, so the shift board
      // said "No waste on this day" for the same facility and day the ledger
      // showed two 12.5 m rows, with nothing on the ledger to reconcile them.
      render: (row) => (
        <>
          <StatusChip variant={dispositionVariant(row.disposition)}>
            {t(`waste.disposition.${row.disposition}`)}
          </StatusChip>
          {row.reversed_by_event_id !== null ? (
            <StatusChip variant="neutral" data-testid="waste-chip-reversed">
              {t("waste.events.reversedChip")}
            </StatusChip>
          ) : null}
          {row.reverses_event_id !== null ? (
            <StatusChip variant="neutral" data-testid="waste-chip-reversal">
              {t("waste.events.reversalChip")}
            </StatusChip>
          ) : null}
        </>
      ),
    },
  ];

  return (
    <section className="waste-events-screen" data-testid="waste-events-screen">
      <PageHeader
        title={t("waste.events.title")}
        actions={
          <>
            <div className="waste-toolbar">
              <Button
                size="sm"
                data-testid="waste-events-csv"
                disabled={rowsAccum.length === 0}
                busy={exportState.kind === "walking"}
                // The house long-operation pattern (Button §3.10 busy/busyLabel),
                // carrying the running row count so a slow month visibly moves.
                busyLabel={
                  exportState.kind === "walking"
                    ? t("waste.events.exporting", { n: exportState.loaded })
                    : undefined
                }
                onClick={() => {
                  void exportCsv();
                }}
              >
                {t("waste.events.exportCsv")}
              </Button>
            </div>
          </>
        }
      />

      {/* The honesty cap bit: the file holds the first N rows of MORE. Said as
          an alert, in the range's own numbers — never a silently partial
          file (GM-week audit). */}
      {exportState.kind === "done" && exportState.capped ? (
        <p className="waste-error-note" role="alert" data-testid="waste-export-capped">
          {t("waste.events.exportCapped", { n: exportState.exported })}
        </p>
      ) : null}

      {/* A failed walk saves NOTHING — the note (the area-shared one: sentence
          + server title + request id) is the only artifact. */}
      {exportState.kind === "failed" ? (
        <MutationFailureNote
          text={t("waste.events.exportFailed")}
          error={exportState.error}
          data-testid="waste-export-failed"
        />
      ) : null}

      <FilterBar
        data-testid="waste-events-filters"
        filters={{
          inline: (
            <>
              <FacilityFilter
                value={facilityId ?? ""}
                facilities={facilities.data?.items ?? []}
                testId="waste-filter-facility"
                allowAll
                onChange={(next) => {
                  setFacilityId(next.length > 0 ? next : undefined);
                  // A station or shift belongs to ONE facility — a facility change
                  // must drop them or the ledger would apply another plant's filter.
                  setWorkCenterId("");
                  setShiftId("");
                }}
              />
              <Field label={t("waste.events.category")}>
                <select
                  value={categoryId}
                  data-testid="waste-filter-category"
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                  }}
                >
                  <option value="">{t("waste.events.allCategories")}</option>
                  {categories.map((row) => (
                    <option key={row.id} value={row.id}>
                      {localeLabel(row.name_i18n, i18n.language) || row.code}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          ),
          more: (
            <>
              <Field label={t("waste.events.from")}>
                <input
                  type="date"
                  value={from}
                  data-testid="waste-filter-from"
                  onChange={(e) => {
                    setFrom(e.target.value);
                  }}
                />
              </Field>
              <Field label={t("waste.events.to")}>
                <input
                  type="date"
                  value={to}
                  data-testid="waste-filter-to"
                  onChange={(e) => {
                    setTo(e.target.value);
                  }}
                />
              </Field>
              <Field label={t("waste.events.colCause")}>
                <select
                  value={causeId}
                  data-testid="waste-filter-cause"
                  onChange={(e) => {
                    setCauseId(e.target.value);
                  }}
                >
                  <option value="">{t("waste.events.allCauses")}</option>
                  {causes.map((row) => (
                    <option key={row.id} value={row.id}>
                      {localeLabel(row.name_i18n, i18n.language) || row.code}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("waste.events.source")}>
                <select
                  value={source}
                  data-testid="waste-filter-source"
                  onChange={(e) => {
                    setSource(e.target.value);
                  }}
                >
                  <option value="">{t("waste.events.allSources")}</option>
                  {SOURCES.map((value) => (
                    <option key={value} value={value}>
                      {t(`waste.source.${value}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("waste.events.disposition")}>
                <select
                  value={disposition}
                  data-testid="waste-filter-disposition"
                  onChange={(e) => {
                    setDisposition(e.target.value);
                  }}
                >
                  <option value="">{t("waste.events.allDispositions")}</option>
                  {(["pending", "reuse", "regrind", "sell", "dispose"] as const).map((value) => (
                    <option key={value} value={value}>
                      {t(`waste.disposition.${value}`)}
                    </option>
                  ))}
                </select>
              </Field>
              {/* 05-B-2 — §4 S2's work-centre / shift / WO filters, driving the
            §5 params that were contract-live before the screen offered them.
            Station + shift are facility-scoped facts, so both wait for a
            facility; the option list SAYS so rather than sitting empty. */}
              <Field label={t("waste.events.workCenter")}>
                <select
                  value={workCenterId}
                  data-testid="waste-filter-work-center"
                  disabled={facilityId === undefined || facilityId.length === 0}
                  onChange={(e) => {
                    setWorkCenterId(e.target.value);
                  }}
                >
                  <option value="">
                    {facilityId === undefined || facilityId.length === 0
                      ? t("waste.events.pickFacilityFirst")
                      : t("waste.events.allWorkCenters")}
                  </option>
                  {(workCenters.data?.items ?? []).map((wc) => (
                    <option key={wc.id} value={wc.id}>
                      {wc.code} · {localeLabel(wc.name_i18n, i18n.language) || wc.code}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("waste.events.shift")}>
                <select
                  value={shiftId}
                  data-testid="waste-filter-shift"
                  disabled={facilityId === undefined || facilityId.length === 0}
                  onChange={(e) => {
                    setShiftId(e.target.value);
                  }}
                >
                  <option value="">
                    {facilityId === undefined || facilityId.length === 0
                      ? t("waste.events.pickFacilityFirst")
                      : t("waste.events.allShifts")}
                  </option>
                  {shifts.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("waste.events.wo")} wide>
                <input
                  value={woId}
                  data-testid="waste-filter-wo"
                  placeholder={t("waste.events.woPlaceholder")}
                  onChange={(e) => {
                    setWoId(e.target.value);
                  }}
                />
                {woId.trim().length > 0 && !woFilterValid ? (
                  <span
                    className="waste-error-note"
                    role="alert"
                    data-testid="waste-filter-wo-invalid"
                  >
                    {t("waste.events.woInvalid")}
                  </span>
                ) : null}
              </Field>
              <Field label={t("waste.events.posted")}>
                <select
                  value={posted}
                  data-testid="waste-filter-posted"
                  onChange={(e) => {
                    setPosted(e.target.value);
                  }}
                >
                  <option value="">{t("waste.events.postedAny")}</option>
                  <option value="true">{t("waste.events.postedYes")}</option>
                  <option value="false">{t("waste.events.postedNo")}</option>
                </select>
              </Field>
            </>
          ),
          // "Filters (2)" once something applies; plain "Filters" at rest.
          label:
            filterChips.length > 0
              ? t("waste.events.filters", { n: filterChips.length })
              : t("waste.events.filtersTitle"),
          title: t("waste.events.filtersTitle"),
          doneLabel: t("waste.events.filtersDone"),
        }}
        chips={filterChips}
        removeLabel={(chip) => t("waste.events.filterRemove", { label: chip.label })}
      />

      {refused ? (
        <ForbiddenState title={t("waste.access.title")} body={t("waste.access.ledger")} />
      ) : null}

      {/* PAUSED, not slow: react-query holds a query while the browser reports
          no network and never times out, so a skeleton here would shimmer for
          as long as the cut lasts and promise nothing. */}
      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("waste.offline.title")} body={t("waste.offline.body")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("waste.common.loading")} />
      ) : null}

      {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("waste.events.error")}
          retryLabel={t("waste.common.retry")}
          requestIdLabel={t("waste.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        <EmptyState title={t("waste.events.emptyTitle")} body={t("waste.events.emptyBody")} />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          <DataTable
            data-testid="waste-events-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            empty={null}
            caption={t("waste.events.tableCaption")}
            rowTestId="waste-event-row"
            rowClassName={(row) =>
              row.reverses_event_id !== null || row.reversed_by_event_id !== null
                ? "waste-row waste-row-reversed"
                : "waste-row"
            }
            onRowClick={(row) => {
              setOpenId(row.id);
            }}
          />

          {page.data?.next_cursor != null ? (
            <div className="waste-toolbar">
              <Button
                size="sm"
                data-testid="waste-events-more"
                onClick={() => {
                  const next = page.data?.next_cursor;
                  if (next != null) setCursors((prior) => [...prior, next]);
                }}
              >
                {t("waste.events.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {openRow !== undefined ? (
        <EventDrawer
          row={openRow}
          categoryName={categoryName(openRow.category_id)}
          causeName={causeName(openRow.cause_code_id)}
          shiftLabel={shifts.labelOf(openRow.shift_instance_id)}
          originShiftLabel={shifts.labelOf(openRow.originating_shift_instance_id)}
          canDecide={canDecide}
          canReverse={canReverse}
          onClose={() => {
            setOpenId(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

// `MutationFailureNote` — the area-shared write-failure note (sentence +
// server title + request id) — moved to ../mutation-failure-note at task #105,
// when S5 was found running a PRIVATE variant that carried none of the three.
// One component, both screens; the history lives in that file's header.

function EventDrawer({
  row,
  categoryName,
  causeName,
  shiftLabel,
  originShiftLabel,
  canDecide,
  canReverse,
  onClose,
}: {
  row: WasteEvent;
  categoryName: string | undefined;
  causeName: string | undefined;
  shiftLabel: string | undefined;
  /** The shift that CAUSED it (D-102), when a board can name it. */
  originShiftLabel: string | undefined;
  canDecide: boolean;
  canReverse: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const decide = useSetWasteDisposition();
  const reverse = useReverseWasteEvent();
  const [disposition, setDisposition] = useState<"reuse" | "regrind" | "sell" | "dispose">(
    "dispose",
  );
  const [reverseReason, setReverseReason] = useState("");
  const [reverseOpen, setReverseOpen] = useState(false);

  const reversed = row.reversed_by_event_id !== null || row.reverses_event_id !== null;

  return (
    <aside className="waste-drawer" role="dialog" aria-modal="false" data-testid="waste-drawer">
      <div className="waste-drawer-head">
        <h2>{row.event_no}</h2>
        <Button size="sm" data-testid="waste-drawer-close" onClick={onClose}>
          {t("waste.common.close")}
        </Button>
      </div>
      <dl className="waste-drawer-facts">
        <dt>{t("waste.events.colWhen")}</dt>
        <dd>{formatTimestamp(row.occurred_at, i18n.language)}</dd>
        <dt>{t("waste.events.colCategory")}</dt>
        <dd>
          <RefLabel value={categoryName} id={row.category_id} />
        </dd>
        {/* The reason gets its OWN row. Appended to the category it inherited
            the empty-cell defect the column fixed: a category with
            `requires_cause: false` has no reason and an unresolved one has an
            unknown reason, and both simply vanished from the sentence. */}
        <dt>{t("waste.events.colCause")}</dt>
        <dd>
          <RefLabel value={causeName} id={row.cause_code_id} />
        </dd>
        <dt>{t("waste.events.colQty")}</dt>
        <dd>
          {formatQty(row.qty, { locale: i18n.language })} {row.uom}
        </dd>
        <dt>{t("waste.events.colCost")}</dt>
        <dd data-testid="waste-drawer-cost">
          {moneyCell(row.cost_minor, row.currency, i18n.language)}
        </dd>
        <dt>{t("waste.events.drawerShift")}</dt>
        {/* THE SHIFT'S NAME FIRST, THE IDENTIFIER AS AN IDENTIFIER. The wire
            carries only the instance id; the operator's word for it is the
            shift-definition code the filter above already resolves. The id
            stays in full underneath because it is what a supervisor pastes back
            into that filter — rendered as `<code>`, so nothing reads it as a
            name. */}
        <dd data-testid="waste-drawer-shift">
          {row.shift_instance_id === null ? (
            t("waste.events.noShift")
          ) : (
            <>
              {shiftLabel !== undefined ? <span>{shiftLabel} </span> : null}
              <code className="ref-id">{row.shift_instance_id}</code>
            </>
          )}
        </dd>
        {/* THE ORIGIN GETS ITS OWN ROW, AND THE CHIP NAMES THE SHIFT.
            §2.3's `originating_shift_instance_id` is D-102's anti-gaming
            mechanic: the shift that CAUSED a defect somebody else found. It
            arrives on the wire (the column is written on three paths, selected
            by every read, serialized outside the money redaction, `required` in
            the OpenAPI document) — and this drawer consumed it as a BOOLEAN and
            discarded the value, so the chip said "another shift" and a night
            boss looking at 412 000 tenge of scrap could not learn whose it was.
            The whole point of the attribution is which one.

            Named the way the LANDING shift beside it is named, and by the same
            join: the shift-definition code off the two-date scoreboard read the
            screen already runs. When no board can name the instance — an origin
            older than yesterday, which a late attribution often is — the id
            stands as an identifier in `<code>` and the chip falls back to the
            sentence it used to be. That is a resolution limit stated honestly,
            not a label forced onto an id. */}
        {row.originating_shift_instance_id !== null ? (
          <>
            <dt>{t("waste.events.drawerOrigin")}</dt>
            <dd data-testid="waste-drawer-origin-shift">
              <StatusChip variant="warn" data-testid="waste-drawer-origin">
                {originShiftLabel !== undefined
                  ? t("waste.events.handedOffFromShift", { shift: originShiftLabel })
                  : t("waste.events.handedOffFrom")}
              </StatusChip>{" "}
              <code className="ref-id">{row.originating_shift_instance_id}</code>
            </dd>
          </>
        ) : null}
        {row.work_order_id !== null ? (
          <>
            <dt>{t("waste.events.drawerWo")}</dt>
            <dd>
              <code className="ref-id">{row.work_order_id}</code>
            </dd>
          </>
        ) : null}
        <dt>{t("waste.events.drawerPhotos")}</dt>
        {/* THE PICTURES, NOT THE COUNT. This cell rendered `row.photo_file_ids.length`
            — so an operator could photograph a damaged drum on the floor, the
            bytes were verified and frozen under `waste_evidence`, and the only
            thing the office could learn was that a photo existed somewhere. */}
        <dd data-testid="waste-drawer-photos">
          <EvidencePhotos fileIds={row.photo_file_ids} data-testid="waste-drawer-photo-strip" />
        </dd>
        {/* D-225: the CORRECTION line is composed here from the row's own
            pointer, not read out of a sentence the server wrote in English. */}
        {row.corrects_event_id !== null && row.corrects_event_no !== null ? (
          <>
            <dt>{t("waste.events.drawerCorrection")}</dt>
            <dd data-testid="waste-drawer-correction">
              {t(
                row.correction_kind === "addition"
                  ? "waste.events.correctionAddition"
                  : "waste.events.correctionRestatement",
                { eventNo: row.corrects_event_no },
              )}
            </dd>
          </>
        ) : null}
        {row.notes !== null ? (
          <>
            <dt>{t("waste.events.drawerNotes")}</dt>
            <dd>{row.notes}</dd>
          </>
        ) : null}
      </dl>

      {/* §3.3: pending → one of four, ONCE. A decided row shows the decision. */}
      {canDecide && row.disposition === "pending" && !reversed ? (
        <div className="waste-drawer-action" data-testid="waste-drawer-decide">
          <Field label={t("waste.events.decideLabel")}>
            <select
              value={disposition}
              data-testid="waste-drawer-disposition"
              onChange={(e) => {
                setDisposition(e.target.value as typeof disposition);
              }}
            >
              {(["reuse", "regrind", "sell", "dispose"] as const).map((value) => (
                <option key={value} value={value}>
                  {t(`waste.disposition.${value}`)}
                </option>
              ))}
            </select>
          </Field>
          <Button
            variant="primary"
            data-testid="waste-drawer-decide-save"
            busy={decide.isPending}
            busyLabel={t("waste.common.saving")}
            onClick={() => {
              decide.mutate({ id: row.id, body: { disposition } });
            }}
          >
            {t("waste.events.decideSave")}
          </Button>
          {decide.isError ? (
            <MutationFailureNote text={t("waste.events.decideFailed")} error={decide.error} />
          ) : null}
        </div>
      ) : null}

      {canReverse && !reversed ? (
        <div className="waste-drawer-action" data-testid="waste-drawer-reverse">
          {reverseOpen ? (
            <>
              <Field label={t("waste.events.reverseReason")}>
                <input
                  value={reverseReason}
                  data-testid="waste-reverse-reason"
                  onChange={(e) => {
                    setReverseReason(e.target.value);
                  }}
                />
              </Field>
              <Button
                variant="danger"
                data-testid="waste-reverse-confirm"
                disabled={reverseReason.length === 0}
                busy={reverse.isPending}
                busyLabel={t("waste.common.saving")}
                onClick={() => {
                  reverse.mutate({ id: row.id, reason: reverseReason });
                }}
              >
                {t("waste.events.reverseConfirm")}
              </Button>
            </>
          ) : (
            <Button
              variant="danger"
              data-testid="waste-reverse-open"
              onClick={() => {
                setReverseOpen(true);
              }}
            >
              {t("waste.events.reverseOpen")}
            </Button>
          )}
          {reverse.isError ? (
            <MutationFailureNote text={t("waste.events.reverseFailed")} error={reverse.error} />
          ) : null}
        </div>
      ) : null}

      {reversed ? (
        <p className="waste-pending-note" data-testid="waste-drawer-reversed-note">
          {t("waste.events.reversedNote")}
        </p>
      ) : null}
    </aside>
  );
}
